import { getLanguageOnlyFromLocale } from "@msys/tolgee";
import { useTolgee, useTranslate } from "@tolgee/react";
import React from "react";
import { SplashScreen } from "../../../common/SplashScreen.js";
import { CreateNewOrganisationPage } from "../CreateNewOrganisationPage.js";
import { useAuth } from "../useAuth.js";
import { useCreateOrganisationAndUser } from "../useCreateOrganisationAndUser.js";
import type { SignupTokenProps } from "../signupRefToken.js";
import { getHandedOverLocale } from "./signupLocale.js";
import {
  deriveSignupOrganisationTitle,
  parseSignupClaims,
} from "./signupClaims.js";

interface Props extends SignupTokenProps {
  /** Called once the creation from the claims starts, before its mutation. */
  onCreationStarted?: () => void;
  onComplete: (userId: string) => void;
}

/**
 * For an account without a platform identity: when the ID token carries the
 * signup claims of the Keycloak registration, creates the organisation from
 * them right away, with exactly one mutation, instead of asking for what the
 * visitor has just typed. Everything else, and every failure, gets the regular
 * organisation-creation page, prefilled with what the claims did provide.
 */
export const SignupClaimsOrganisationCreator = ({
  onCreationStarted,
  onComplete,
  ...tokenProps
}: Props) => {
  const { t } = useTranslate(["Global"]);
  const tolgee = useTolgee(["language"]);
  const { auth } = useAuth();
  const { createOrganisationAndUser, handleSignupError } =
    useCreateOrganisationAndUser();

  const claims = React.useMemo(
    () => parseSignupClaims(auth.idTokenParsed),
    [auth.idTokenParsed]
  );
  const title = claims ? deriveSignupOrganisationTitle(claims) : null;
  const organisationType = claims?.organisationType;
  const { referral, refToken } = tokenProps;

  const [hasFailed, setHasFailed] = React.useState(false);
  // The language picked on the Keycloak pages wins over the app's own, unless
  // the app already shows a variant of it (e.g. `de-RF` for `de`). It switches
  // once per page load; the promise resolves with the locale to create the user
  // with, `undefined` when the app language stays.
  const languageSwitchRef = React.useRef<Promise<string | undefined> | null>(
    null
  );
  const idToken = auth.idTokenParsed;
  const ensureHandedOverLanguage = React.useCallback((): Promise<
    string | undefined
  > => {
    if (languageSwitchRef.current !== null) {
      return languageSwitchRef.current;
    }
    const current = tolgee.getLanguage();
    const locale = getHandedOverLocale(idToken, current);
    const started: Promise<string | undefined> =
      locale === null ||
      (current !== undefined &&
        getLanguageOnlyFromLocale(locale) ===
          getLanguageOnlyFromLocale(current))
        ? Promise.resolve(undefined)
        : tolgee.changeLanguage(locale).then(
            () => locale,
            () => undefined
          );
    languageSwitchRef.current = started;
    return started;
  }, [idToken, tolgee]);

  React.useEffect(() => {
    void ensureHandedOverLanguage();
  }, [ensureHandedOverLanguage]);

  // One attempt per page load: effects run twice under StrictMode and again
  // whenever a dependency changes, a failure must not start another attempt.
  const hasStartedRef = React.useRef(false);

  React.useEffect(() => {
    if (organisationType === undefined || title === null) {
      return;
    }
    if (hasStartedRef.current) {
      return;
    }
    hasStartedRef.current = true;
    onCreationStarted?.();

    ensureHandedOverLanguage()
      .then(locale =>
        createOrganisationAndUser({
          organisationType,
          title,
          referral,
          refToken,
          locale,
        })
      )
      .then(
        userId => onComplete(userId),
        (error: unknown) => {
          setHasFailed(true);
          handleSignupError(error);
        }
      );
  }, [
    createOrganisationAndUser,
    ensureHandedOverLanguage,
    handleSignupError,
    onComplete,
    onCreationStarted,
    organisationType,
    refToken,
    referral,
    title,
  ]);

  if (organisationType === undefined || title === null || hasFailed) {
    return (
      <CreateNewOrganisationPage
        {...tokenProps}
        initialOrganisationName={
          claims?.companyName ?? tokenProps.initialOrganisationName
        }
        initialOrganisationType={organisationType}
        onComplete={onComplete}
      />
    );
  }

  return (
    <SplashScreen message={t("Preparing account ...", { ns: "Global" })} />
  );
};
